<template>
  <div class="vod">
    <APageHead :title="org?.name || '기관 상세'" :sub="headSub" :back="true" @back="$router.push({ name: 'VoucherOrganizations' })">
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="reload">새로고침</ABtn>
        <template v-if="org">
          <ABtn kind="ghost" icon="edit" @click="formOpen = true">정보 수정</ABtn>
          <ABtn v-if="isActive" kind="danger" icon="block" @click="deactivate">비활성화</ABtn>
          <ABtn v-else kind="primary" icon="play_arrow" @click="activate">활성화</ABtn>
        </template>
      </template>
    </APageHead>

    <div v-if="loading && !org" class="vod__loading"><div class="p-spinner p-spinner--lg"></div></div>
    <LoadError v-else-if="!org && loadError" title="기관 정보를 불러오지 못했어요" :message="loadError" @retry="reload" />
    <AEmpty v-else-if="!org" title="기관을 찾을 수 없습니다" />

    <template v-else>
      <div v-if="!isActive" class="vod__banner vod__banner--muted">
        <i class="material-icons-round">block</i>
        비활성 기관입니다. Master 기능과 학생 등록이 멈춰 있습니다. 남은 잔액은 잔액 탭의 「반환」으로 정리해 주세요.
      </div>
      <div v-if="isActive && masterCount === 0" class="vod__banner vod__banner--err">
        <i class="material-icons-round">warning</i>
        이 기관에는 Master 가 없습니다. 멤버 탭에서 승인 강사를 Master 로 지정해 주세요.
      </div>

      <div class="p-tabs">
        <button
          v-for="t in TABS"
          :key="t.id"
          type="button"
          :class="['p-tabs__tab', { 'is-active': tab === t.id }]"
          @click="switchTab(t.id)"
        >
          <i class="material-icons-round vod__tab-icon">{{ t.icon }}</i>
          {{ t.label }}
        </button>
      </div>

      <!-- ── 정보 ── -->
      <template v-if="tab === 'info'">
        <ACard title="기관 정보">
          <dl class="vod__dl">
            <div><dt>기관명</dt><dd>{{ org.name }}<span v-if="org.name_en" class="vod__sub"> · {{ org.name_en }}</span></dd></div>
            <div><dt>바우처 통화</dt><dd class="vod__mono">{{ currency }}</dd></div>
            <div><dt>기관 할인율</dt><dd>{{ percent(org.voucher_discount_rate) }}</dd></div>
            <div><dt>국가</dt><dd>{{ org.country_name || org.country?.name || (org.country_idx ? `#${org.country_idx}` : '-') }}</dd></div>
            <div><dt>담당자</dt><dd>{{ [org.contact_name, org.contact_email, org.contact_phone].filter(Boolean).join(' · ') || '-' }}</dd></div>
            <div><dt>주소</dt><dd>{{ org.address || '-' }}</dd></div>
            <div><dt>계약 기간</dt><dd>{{ org.contract_start || '-' }} ~ {{ org.contract_end || '-' }}</dd></div>
            <div><dt>메모</dt><dd class="vod__pre">{{ org.memo || '-' }}</dd></div>
            <div v-if="org.deactivated_at || org.deactivate_reason">
              <dt>비활성화</dt><dd>{{ fmtKst(org.deactivated_at) }} · {{ org.deactivate_reason || '-' }}</dd>
            </div>
          </dl>
        </ACard>
        <ACard title="변경 이력">
          <template #action>
            <router-link class="vod__more" :to="{ name: 'SystemLogsHistory', query: { tab: 'vouchers', organizationIdx: orgIdx } }">
              바우처 이력 전체 보기 <i class="material-icons-round">chevron_right</i>
            </router-link>
          </template>
          <div v-if="history.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="history.error" size="sm" :message="history.error" @retry="loadHistory" />
          <AEmpty v-else-if="!history.items.length" size="sm" title="이력이 없습니다" />
          <template v-else>
            <ATable :columns="historyCols" :rows="history.items" row-key="idx">
              <template #cell:at="{ row }"><span class="vod__mono vod__small">{{ fmtKst(row.at) }}</span></template>
              <template #cell:event="{ row }"><APill :kind="historyEventKind(row.event)" :title="row.event">{{ historyEventLabel(row.event) }}</APill></template>
              <template #cell:actor="{ row }">
                <div class="vod__stack">
                  <span>{{ historyActorLabel(row.actor) }}</span>
                  <span v-if="row.actor" class="vod__sub">{{ historyActorType(row.actor) }}</span>
                </div>
              </template>
              <template #cell:changes="{ row }">
                <div class="vod__changes">
                  <span v-for="c in historyChanges(row)" :key="c.key" class="vod__change">
                    <b>{{ c.key }}</b> {{ c.before }} → {{ c.after }}
                  </span>
                  <span v-for="c in historyDetailChips(row)" :key="'d-' + c.key" class="vod__change">
                    <b>{{ c.key }}</b> {{ c.value }}
                  </span>
                  <span v-if="row.reason" class="vod__sub">{{ row.reason }}</span>
                </div>
              </template>
            </ATable>
            <APagination :current-page="history.page" :total="history.total" :page-size="history.size" @change="(p) => { history.page = p; loadHistory(); }" />
          </template>
        </ACard>
      </template>

      <!-- ── 잔액 ── -->
      <template v-if="tab === 'balance'">
        <div v-if="balance.error" class="vod__banner vod__banner--err">
          <i class="material-icons-round">error</i>
          <span>잔액 요약을 불러오지 못했습니다 — {{ balance.error }}</span>
          <ABtn size="sm" kind="ghost" icon="refresh" @click="loadBalance">다시 시도</ABtn>
        </div>
        <div v-if="balance.data && (balance.data.ledger_consistent === false || balance.data.identity_consistent === false)" class="vod__banner vod__banner--err">
          <i class="material-icons-round">error</i>
          <span>
            잔액 검증이 맞지 않습니다
            <template v-if="balance.data.ledger_consistent === false">— 잔액과 원장 합계가 다릅니다</template>
            <template v-if="balance.data.identity_consistent === false">— 확정 구매 − 반환 ± 정정({{ money(balance.data.identity_lhs, currency) }}) 이 잔액 + 사용액 + 처리 중({{ money(balance.data.identity_rhs, currency) }}) 과 다릅니다</template>.
            정정하기 전에 플랫폼 백엔드에 알려 주세요.
          </span>
        </div>
        <div class="vod__stats">
          <AStat label="잔액" :value="money(bal.voucher_balance, currency)">
            <div class="vod__stat-badges">
              <APill v-if="balance.data?.ledger_consistent === true && balance.data?.identity_consistent === true" kind="ok" size="sm">검증 일치</APill>
            </div>
          </AStat>
          <AStat label="누계 확정 구매" :value="money(bal.purchased_total, currency)">
            <div v-if="isPositive(bal.voided_total)" class="vod__stat-sub">확정 취소 {{ money(bal.voided_total, currency) }}</div>
          </AStat>
          <AStat label="누계 사용" :value="money(bal.used_amount, currency)">
            <div class="vod__stat-sub">{{ bal.used_count ?? 0 }}건</div>
          </AStat>
          <AStat label="처리 중" :value="money(bal.pending_amount, currency)">
            <div class="vod__stat-sub">{{ bal.pending_count ?? 0 }}건</div>
          </AStat>
          <AStat label="누계 반환" :value="money(bal.withdrawn_total, currency)" />
          <AStat label="누계 정정" :value="money(bal.adjusted_total, currency)" />
          <AStat label="이번 달 적립 · 사용" :value="money(bal.month_purchased, currency)">
            <div class="vod__stat-sub">사용 {{ money(bal.month_registered, currency) }}</div>
          </AStat>
          <AStat label="등록 대기 예상액" :value="money(bal.pending_estimate, currency)">
            <div v-if="isPositive(bal.balance_shortfall)" class="vod__stat-err">
              부족 예상 {{ money(bal.balance_shortfall, currency) }}
            </div>
          </AStat>
        </div>

        <ACard overflow="visible">
          <template #title>원장</template>
          <template #action>
            <div class="vod__actions">
              <ABtn size="sm" kind="ghost" icon="south_west" :disabled="!isPositive(bal.voucher_balance)" @click="openMove('withdraw')">반환</ABtn>
              <ABtn v-if="canAdjust" size="sm" kind="ghost" icon="tune" @click="openMove('adjust')">정정</ABtn>
            </div>
          </template>
          <div class="vod__filters">
            <div class="p-field">
              <label class="p-field__label">종류</label>
              <select v-model="ledger.filters.entry_type" class="p-input" @change="searchLedger">
                <option value="">전체</option>
                <option v-for="t in LEDGER_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
            </div>
            <div class="p-field">
              <label class="p-field__label">시작일 (KST)</label>
              <input v-model="ledger.filters.date_from" class="p-input" type="date" />
            </div>
            <div class="p-field">
              <label class="p-field__label">종료일 (KST)</label>
              <input v-model="ledger.filters.date_to" class="p-input" type="date" />
            </div>
            <div class="vod__filter-actions">
              <ABtn kind="primary" icon="search" @click="searchLedger">조회</ABtn>
            </div>
          </div>
          <div v-if="ledger.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="ledger.error" size="sm" :message="ledger.error" @retry="loadLedger" />
          <AEmpty v-else-if="!ledger.items.length" size="sm" title="원장 항목이 없습니다" />
          <template v-else>
            <ATable :columns="ledgerCols" :rows="ledger.items" row-key="idx">
              <template #cell:at="{ row }"><span class="vod__mono vod__small">{{ fmtKst(row.created_at) }}</span></template>
              <template #cell:type="{ row }"><APill :kind="ledgerTypeKind(row.entry_type)">{{ ledgerTypeLabel(row.entry_type) }}</APill></template>
              <template #cell:amount="{ row }">
                <b :class="['vod__mono', Number(row.amount) < 0 ? 'vod__neg' : 'vod__pos']">{{ signedMoney(row.amount, row.currency_code || currency) }}</b>
              </template>
              <template #cell:balance_after="{ row }"><span class="vod__mono">{{ money(row.balance_after, row.currency_code || currency) }}</span></template>
              <template #cell:desc="{ row }">
                <div class="vod__stack">
                  <span>{{ ledgerDesc(row) }}</span>
                  <span class="vod__sub">
                    {{ [row.memo, row.actor_emp_name || row.actor_name].filter(Boolean).join(' · ') }}
                  </span>
                </div>
              </template>
            </ATable>
            <APagination :current-page="ledger.page" :total="ledger.total" :page-size="ledger.size" @change="(p) => { ledger.page = p; loadLedger(); }" />
          </template>
        </ACard>
      </template>

      <!-- ── 구매 · 입금 ── -->
      <template v-if="tab === 'purchases'">
        <ACard>
          <template #title>구매 · 입금</template>
          <template #action>
            <ABtn size="sm" kind="primary" icon="add" :disabled="!isActive" @click="openDirect">입금 직접 등록</ABtn>
          </template>
          <div v-if="purchases.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="purchases.error" size="sm" :message="purchases.error" @retry="loadPurchases" />
          <AEmpty v-else-if="!purchases.items.length" size="sm" title="구매 · 입금 기록이 없습니다" />
          <ATable v-else :columns="purchaseCols" :rows="purchases.items" row-key="idx">
            <template #cell:ref="{ row }">
              <div class="vod__stack">
                <span class="vod__mono vod__strong">{{ row.reference || `VP-${row.idx}` }}</span>
                <span class="vod__sub">{{ fmtKst(row.created_at) }}</span>
              </div>
            </template>
            <template #cell:requested="{ row }">
              <span v-if="row.requested_amount != null" class="vod__mono">{{ money(row.requested_amount, row.requested_currency) }}</span>
              <span v-else class="vod__sub">직접 등록</span>
            </template>
            <template #cell:deposit="{ row }">
              <div v-if="row.paid_amount != null" class="vod__stack">
                <span class="vod__mono">{{ money(row.paid_amount, row.paid_currency) }}</span>
                <span v-if="row.credited_amount != null && row.paid_currency !== purchaseCurrency(row)" class="vod__sub vod__mono">
                  적립 {{ money(row.credited_amount, purchaseCurrency(row)) }}
                </span>
                <span class="vod__sub">{{ row.deposited_on || '' }}{{ row.payment_ref ? ` · ${row.payment_ref}` : '' }}</span>
              </div>
              <span v-else class="vod__dash">—</span>
            </template>
            <template #cell:status="{ row }">
              <div class="vod__stack vod__stack--center">
                <APill :kind="purchaseStatusKind(row.status)" dot>{{ purchaseStatusLabel(row.status) }}</APill>
                <span v-if="row.cancel_reason" class="vod__sub" :title="row.cancel_reason">{{ row.cancel_reason }}</span>
              </div>
            </template>
            <template #cell:actions="{ row }">
              <ABtn v-if="row.status === 'REQUESTED'" size="sm" kind="primary" icon="check" @click="openConfirm(row)">입금 확인</ABtn>
            </template>
          </ATable>
          <p class="vod__note">반려 · 확정 취소는 <router-link :to="{ name: 'VoucherPurchases', query: { organization_idx: orgIdx } }">바우처 · 구매 확정</router-link> 에서 합니다.</p>
        </ACard>
      </template>

      <!-- ── 멤버 ── -->
      <template v-if="tab === 'members'">
        <ACard>
          <template #title>멤버</template>
          <template #action>
            <div class="vod__actions">
              <!--
                목록 기본값은 활성 멤버만입니다(서버 status=active, 2026-09-23). 해제된 강사는 여기서 보이지 않습니다.
                해제 이력을 볼 때만 status 를 바꿉니다.
              -->
              <select v-model="members.status" class="p-input vod__mini-select" @change="loadMembers">
                <option value="active">활성 멤버</option>
                <option value="released">해제된 멤버</option>
                <option value="all">전체</option>
              </select>
              <ABtn size="sm" kind="primary" icon="mail" :disabled="!isActive" @click="openInvite()">강사 등록 요청</ABtn>
              <ABtn
                size="sm"
                kind="ghost"
                icon="stars"
                :disabled="!isActive"
                @click="$router.push({ name: 'VoucherMasters', query: { organization_idx: orgIdx } })"
              >Master 지정</ABtn>
            </div>
          </template>
          <p class="vod__note vod__note--top">
            강사는 <b>본인 동의</b>가 있어야 소속이 됩니다 — 이메일로 요청하면 강사에게 6자리 코드가 가고, 그 코드를 입력해야 등록됩니다.
            Master 지정은 계약 근거로 하는 것이라 동의 절차 없이 Master 관리에서 합니다.
          </p>
          <div v-if="members.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="members.error" size="sm" :message="members.error" @retry="loadMembers" />
          <AEmpty
            v-else-if="!members.items.length"
            size="sm"
            :title="members.status === 'released' ? '해제된 멤버가 없습니다' : '멤버가 없습니다'"
          />
          <ATable v-else :columns="memberCols" :rows="members.items" row-key="users_idx">
            <template #cell:user="{ row }">
              <div class="vod__stack">
                <span class="vod__strong">{{ row.name || '-' }}</span>
                <span class="vod__sub">{{ row.email || '' }}</span>
              </div>
            </template>
            <template #cell:role="{ row }">
              <div class="vod__stack vod__stack--center">
                <APill :kind="memberRoleKind(row.role)">{{ memberRoleLabel(row.role) }}</APill>
                <APill v-if="isReleasedMember(row)" kind="default" size="sm">해제됨</APill>
                <APill v-else-if="row.user_type && row.user_type !== 'instructor'" kind="err" size="sm">강사 권한 없음</APill>
              </div>
            </template>
            <template #cell:joined="{ row }">
              <div class="vod__stack">
                <span class="vod__mono vod__small">{{ fmtKst(row.created_at) }}</span>
                <span class="vod__sub">{{ row.created_by_emp_name ? `관리자 ${row.created_by_emp_name}` : row.created_by_name ? `Master ${row.created_by_name}` : '' }}</span>
                <span v-if="isReleasedMember(row)" class="vod__sub">
                  해제 {{ fmtKst(row.deactivated_at) }}{{ row.deactivate_reason ? ` · ${row.deactivate_reason}` : '' }}
                </span>
              </div>
            </template>
            <template #cell:classes="{ row }">
              <span class="vod__mono">{{ row.ongoing_class_count ?? 0 }} · {{ row.student_count ?? 0 }}명</span>
            </template>
            <template #cell:actions="{ row }">
              <!-- 해제된 행은 활성 멤버가 아니라 역할 변경 · 해제가 409/404 입니다. 버튼을 두지 않습니다. -->
              <div v-if="!isReleasedMember(row)" class="vod__actions">
                <ABtn size="sm" kind="ghost" icon="swap_horiz" :title="row.role === 'master' ? '강사로 변경' : 'Master 로 변경'" @click="changeRole(row)">
                  {{ row.role === 'master' ? '강사로' : 'Master 로' }}
                </ABtn>
                <ABtn size="sm" kind="danger" icon="person_remove" icon-only title="해제" @click="removeMember(row)" />
              </div>
            </template>
          </ATable>
        </ACard>

        <!-- 동의 코드를 기다리는 등록 요청. 24시간이 지나면 만료되고 서버가 EXPIRED 로 돌립니다. -->
        <ACard>
          <template #title>등록 대기</template>
          <template #action>
            <div class="vod__actions">
              <select v-model="invites.status" class="p-input vod__mini-select" @change="loadInvites">
                <option value="PENDING">대기 중</option>
                <option value="CONFIRMED">완료</option>
                <option value="CANCELLED">취소</option>
                <option value="EXPIRED">만료</option>
                <option value="all">전체</option>
              </select>
              <ABtn size="sm" kind="ghost" icon="refresh" @click="loadInvites">새로고침</ABtn>
            </div>
          </template>
          <div v-if="invites.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="invites.error" size="sm" :message="invites.error" @retry="loadInvites" />
          <AEmpty v-else-if="!invites.items.length" size="sm" title="대기 중인 등록 요청이 없습니다" />
          <ATable v-else :columns="inviteCols" :rows="invites.items" row-key="invite_idx">
            <template #cell:user="{ row }">
              <div class="vod__stack">
                <!-- 동의 전에는 서버가 이름을 가려서 줍니다(김**). 확인되면 실명으로 바뀝니다. -->
                <span class="vod__strong">{{ row.name || '-' }}</span>
                <span class="vod__sub">{{ row.email || '' }}</span>
              </div>
            </template>
            <template #cell:status="{ row }">
              <APill :kind="inviteStatusKind(row.status)" dot>{{ inviteStatusLabel(row.status) }}</APill>
            </template>
            <template #cell:expires="{ row }">
              <div class="vod__stack">
                <span class="vod__mono vod__small">{{ fmtKst(row.expires_at) }}</span>
                <span v-if="row.last_sent_at" class="vod__sub vod__mono">최근 발송 {{ fmtKst(row.last_sent_at) }}</span>
              </div>
            </template>
            <template #cell:counts="{ row }">
              <div class="vod__stack vod__stack--center">
                <span class="vod__mono">발송 {{ row.sent_count ?? 0 }}{{ row.max_sends ? ' / ' + row.max_sends : '' }}</span>
                <span class="vod__sub vod__mono">잘못 입력 {{ row.attempts ?? 0 }}{{ row.max_attempts ? ' / ' + row.max_attempts : '' }}</span>
              </div>
            </template>
            <template #cell:by="{ row }">
              <div class="vod__stack">
                <span>{{ row.requested_by_emp_name || row.requested_by_name || '-' }}</span>
                <span class="vod__sub">{{ row.requested_by_emp_name ? '관리자' : row.requested_by_name ? 'Master' : '' }} · {{ fmtKst(row.created_at) }}</span>
              </div>
            </template>
            <template #cell:actions="{ row }">
              <div v-if="row.status === 'PENDING'" class="vod__actions">
                <ABtn size="sm" kind="primary" icon="pin" @click="openInvite(row)">코드 입력</ABtn>
                <ABtn size="sm" kind="danger" icon="close" icon-only title="요청 취소" @click="cancelInvite(row)" />
              </div>
            </template>
          </ATable>
        </ACard>
      </template>

      <!-- ── 클래스 바우처 (기관 Master API 를 emp 토큰으로 그대로 호출) ── -->
      <template v-if="tab === 'classes'">
        <ACard overflow="visible">
          <template #title>클래스별 사용 통계</template>
          <div class="vod__filters">
            <div class="p-field">
              <label class="p-field__label">클래스 상태</label>
              <select v-model="classStats.filters.class_status" class="p-input" @change="loadClassStats">
                <option value="ongoing">진행 중</option>
                <option value="closed">종료</option>
                <option value="all">전체</option>
              </select>
            </div>
            <div class="p-field">
              <label class="p-field__label">시작일 (KST)</label>
              <input v-model="classStats.filters.date_from" class="p-input" type="date" />
            </div>
            <div class="p-field">
              <label class="p-field__label">종료일 (KST)</label>
              <input v-model="classStats.filters.date_to" class="p-input" type="date" />
            </div>
            <div class="vod__filter-actions">
              <ABtn kind="primary" icon="search" @click="loadClassStats">조회</ABtn>
            </div>
          </div>
          <div v-if="classStats.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="classStats.error" size="sm" :message="classStats.error" @retry="loadClassStats" />
          <AEmpty v-else-if="!classStats.items.length" size="sm" title="바우처가 지정된 클래스가 없습니다" />
          <ATable v-else :columns="classStatCols" :rows="classStatRows" row-key="classroom_idx">
            <template #cell:classroom="{ row }">
              <div class="vod__stack">
                <span :class="{ 'vod__strong': !row.__total }">{{ row.__total ? '합계' : (row.name || `#${row.classroom_idx}`) }}</span>
                <span v-if="!row.__total" class="vod__sub">{{ row.instructor_name || '' }}{{ row.end_date ? ` · ~${row.end_date}` : '' }}</span>
              </div>
            </template>
            <template #cell:students="{ row }"><span class="vod__mono">{{ row.student_count ?? '-' }}</span></template>
            <template #cell:books="{ row }"><span class="vod__mono">{{ row.__total ? '' : (row.assignment_count ?? '-') }}</span></template>
            <template #cell:regs="{ row }">
              <span class="vod__mono">{{ row.registered_count ?? '-' }} / {{ row.unregistered_count ?? '-' }}</span>
            </template>
            <template #cell:rate="{ row }"><span class="vod__mono">{{ row.registration_rate != null ? percent(row.registration_rate) : '-' }}</span></template>
            <template #cell:used="{ row }"><span class="vod__mono">{{ money(row.registered_amount, classStats.currency || currency) }}</span></template>
            <template #cell:estimate="{ row }"><span class="vod__mono">{{ money(row.pending_estimate, classStats.currency || currency) }}</span></template>
          </ATable>
        </ACard>

        <ACard title="클래스 지정 현황" sub="진행 중 클래스에 지정된 도서 · 플랜입니다. 지정 · 종료는 기관 Master 가 강사 화면에서 합니다.">
          <div v-if="classrooms.loading" class="vod__loading"><div class="p-spinner"></div></div>
          <LoadError v-else-if="classrooms.error" size="sm" :message="classrooms.error" @retry="loadClassrooms" />
          <AEmpty v-else-if="!classrooms.items.length" size="sm" title="진행 중 클래스가 없습니다" />
          <ATable v-else :columns="classroomCols" :rows="classrooms.items" row-key="classroom_idx">
            <template #cell:classroom="{ row }">
              <div class="vod__stack">
                <span class="vod__strong">{{ row.classroom_name || row.name || `#${row.classroom_idx}` }}</span>
                <span class="vod__sub">{{ row.instructor_name || row.instructor?.name || '' }}</span>
              </div>
            </template>
            <template #cell:assignments="{ row }">
              <div class="vod__changes">
                <span v-for="a in row.assignments || []" :key="a.idx || a.classroom_voucher_idx" class="vod__change">
                  {{ a.textbook?.title || a.title || a.barcode }}
                  <APill size="sm" kind="info">{{ gradeLabel(a.grade) || a.grade }}</APill>
                  <span class="vod__mono">{{ a.registered_count ?? 0 }}{{ a.max_registrations ? `/${a.max_registrations}` : '' }}</span>
                </span>
                <span v-if="!(row.assignments || []).length" class="vod__dash">지정 없음</span>
              </div>
            </template>
          </ATable>
        </ACard>
      </template>
    </template>

    <!-- 반환 / 정정 -->
    <AModal v-model="move.open" size="sm" :title="move.mode === 'adjust' ? '잔액 정정' : '잔액 반환'" :close-on-backdrop="!move.saving">
      <div class="vod__form">
        <p v-if="move.mode === 'withdraw'" class="vod__note vod__note--top">
          기관에 돌려준 금액을 기록합니다. 현재 잔액 {{ money(bal.voucher_balance ?? org?.voucher_balance, currency) }}.
        </p>
        <p v-else class="vod__note vod__note--top">
          오류를 바로잡을 때만 씁니다. 늘리려면 양수, 줄이려면 음수(−)로 입력합니다. 원장에 사유와 함께 남습니다.
        </p>
        <div class="p-field">
          <label class="p-field__label">금액 ({{ currency }}) <span class="p-field__req">*</span></label>
          <input v-model.trim="move.amount" class="p-input" type="text" inputmode="decimal" :placeholder="move.mode === 'adjust' ? '예: -12.50' : '0.00'" />
        </div>
        <div v-if="move.mode === 'withdraw'" class="p-field">
          <label class="p-field__label">송금 참조번호</label>
          <input v-model.trim="move.payment_ref" class="p-input" type="text" maxlength="100" />
        </div>
        <div class="p-field">
          <label class="p-field__label">사유 <span class="p-field__req">*</span></label>
          <textarea v-model.trim="move.memo" class="p-input" rows="3" maxlength="500"></textarea>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" :disabled="move.saving" @click="move.open = false">취소</ABtn>
        <ABtn kind="primary" :loading="move.saving" @click="submitMove">{{ move.mode === 'adjust' ? '정정' : '반환 기록' }}</ABtn>
      </template>
    </AModal>

    <InstructorInviteModal
      v-model="inviteModal.open"
      :organization-idx="orgIdx"
      :organization-name="org?.name || ''"
      :initial-invite="inviteModal.invite"
      @done="onInviteDone"
    />
    <OrganizationFormModal v-model="formOpen" :organization="org" @saved="reload" />
    <PurchaseConfirmModal
      v-model="depositOpen"
      :mode="depositMode"
      :purchase="depositTarget"
      :organization="orgForModal"
      @done="onDeposited"
    />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import InstructorInviteModal from "@/views/voucher/components/InstructorInviteModal.vue";
import LoadError from "@/views/voucher/components/LoadError.vue";
import OrganizationFormModal from "@/views/voucher/components/OrganizationFormModal.vue";
import PurchaseConfirmModal from "@/views/voucher/components/PurchaseConfirmModal.vue";
import { gradeLabel } from "@/utils/paymentDisplay";
import {
  fmtKst, money, signedMoney, percent, isPositive, pageOf, bodyOf, errorCode, errorDetail, errorMessage, loadErrorText,
  LEDGER_TYPES, ledgerTypeLabel, ledgerTypeKind, purchaseStatusLabel, purchaseStatusKind, purchaseCurrency,
  historyEventLabel, historyEventKind, historyChanges, historyDetailChips, historyActorLabel, historyActorType,
  memberRoleLabel, memberRoleKind, ADJUST_ROLES,
} from "@/utils/voucher";
import { confirmCloseVouchers } from "@/views/voucher/components/activeVouchers";

const TABS = [
  { id: "info", label: "정보 · 이력", icon: "apartment" },
  { id: "balance", label: "잔액 · 원장", icon: "account_balance_wallet" },
  { id: "purchases", label: "구매 · 입금", icon: "payments" },
  { id: "members", label: "멤버", icon: "group" },
  { id: "classes", label: "클래스 바우처", icon: "meeting_room" },
];

// 등록 요청 상태(가이드 §4-3). PENDING 만 코드 입력 · 재발송 · 취소가 됩니다.
const INVITE_STATUS = {
  PENDING: { label: "대기 중", kind: "warn" },
  CONFIRMED: { label: "완료", kind: "ok" },
  CANCELLED: { label: "취소", kind: "default" },
  EXPIRED: { label: "만료", kind: "err" },
};

const DECIMAL = /^\d+(\.\d{1,2})?$/;
const SIGNED_DECIMAL = /^-?\d+(\.\d{1,2})?$/;

export default {
  name: "VoucherOrganizationDetail",
  components: {
    APageHead, ACard, ABtn, APill, AStat, ATable, AModal, AEmpty, APagination,
    OrganizationFormModal, PurchaseConfirmModal, InstructorInviteModal, LoadError,
  },
  props: {
    organizationIdx: { type: [String, Number], required: true },
  },
  data() {
    return {
      TABS,
      LEDGER_TYPES,
      tab: TABS.some((t) => t.id === this.$route.query.tab) ? this.$route.query.tab : "info",
      loading: false,
      loadError: "",
      org: null,
      summary: null,
      history: { items: [], total: 0, page: 1, size: 20, loading: false, loaded: false, error: "" },
      balance: { data: null, loading: false, error: "" },
      ledger: { items: [], total: 0, page: 1, size: 20, loading: false, loaded: false, error: "", filters: { entry_type: "", date_from: "", date_to: "" } },
      purchases: { items: [], loading: false, loaded: false, error: "" },
      members: { items: [], status: "active", loading: false, loaded: false, error: "" },
      classStats: { items: [], totals: null, currency: null, loading: false, loaded: false, error: "", filters: { class_status: "ongoing", date_from: "", date_to: "" } },
      classrooms: { items: [], loading: false, error: "" },
      formOpen: false,
      depositOpen: false,
      depositMode: "direct",
      depositTarget: null,
      move: { open: false, mode: "withdraw", amount: "", payment_ref: "", memo: "", saving: false },
      invites: { items: [], status: "PENDING", loading: false, loaded: false, error: "" },
      inviteModal: { open: false, invite: null },
      historyCols: [
        { key: "at", label: "시각 (KST)", width: 140 },
        { key: "event", label: "내용", width: 150 },
        { key: "actor", label: "수행자", width: 140 },
        { key: "changes", label: "변경", wrap: true },
      ],
      ledgerCols: [
        { key: "at", label: "시각 (KST)", width: 140 },
        { key: "type", label: "종류", width: 100, align: "center" },
        { key: "amount", label: "금액", width: 150, align: "right" },
        { key: "balance_after", label: "잔액", width: 150, align: "right" },
        { key: "desc", label: "내용", wrap: true },
      ],
      purchaseCols: [
        { key: "ref", label: "구매", width: 160 },
        { key: "requested", label: "요청 금액", width: 150 },
        { key: "deposit", label: "입금", wrap: true },
        { key: "status", label: "상태", width: 110, align: "center" },
        { key: "actions", label: "", width: 120, align: "right" },
      ],
      inviteCols: [
        { key: "user", label: "강사", width: 220 },
        { key: "status", label: "상태", width: 100, align: "center" },
        { key: "expires", label: "만료 (KST)", width: 170 },
        { key: "counts", label: "발송 · 입력", width: 140, align: "center" },
        { key: "by", label: "요청자", width: 180 },
        { key: "actions", label: "", width: 170, align: "right" },
      ],
      memberCols: [
        { key: "user", label: "강사", width: 260 },
        { key: "role", label: "역할", width: 130, align: "center" },
        { key: "joined", label: "등록 (KST)", width: 140 },
        { key: "classes", label: "진행 클래스", width: 100, align: "center" },
        { key: "actions", label: "", width: 180, align: "right" },
      ],
      classStatCols: [
        { key: "classroom", label: "클래스", width: 220 },
        { key: "students", label: "수강생", width: 80, align: "center" },
        { key: "books", label: "지정 도서", width: 90, align: "center" },
        { key: "regs", label: "등록 / 미등록", width: 120, align: "center" },
        { key: "rate", label: "등록률", width: 90, align: "center" },
        { key: "used", label: "차감액", width: 130, align: "right" },
        { key: "estimate", label: "예상액", width: 130, align: "right" },
      ],
      classroomCols: [
        { key: "classroom", label: "클래스", width: 240 },
        { key: "assignments", label: "지정 도서 · 등록 수", wrap: true },
      ],
    };
  },
  computed: {
    orgIdx() {
      return Number(this.organizationIdx);
    },
    currency() {
      return this.org?.voucher_currency || "USD";
    },
    // 잔액 탭 요약(GET …/balance). 로드 전에는 빈 객체라 템플릿이 "-" 로 그립니다.
    bal() {
      return this.balance.data || {};
    },
    isActive() {
      return this.org?.active_yn !== "N";
    },
    masterCount() {
      const fromSummary = this.summary?.master_count ?? this.org?.master_count;
      if (fromSummary !== undefined && fromSummary !== null) return Number(fromSummary);
      return this.members.items.filter((m) => m.role === "master" && !this.isReleasedMember(m)).length;
    },
    headSub() {
      if (!this.org) return "";
      return `${this.currency} · 할인 ${percent(this.org.voucher_discount_rate)} · 잔액 ${money(this.org.voucher_balance ?? this.summary?.voucher_balance, this.currency)}`;
    },
    canAdjust() {
      return ADJUST_ROLES.includes(this.$store.getters["auth/getRoleCode"]);
    },
    orgForModal() {
      return this.org ? { ...this.org, idx: this.orgIdx } : null;
    },
    classStatRows() {
      const rows = [...this.classStats.items];
      if (this.classStats.totals) rows.push({ ...this.classStats.totals, classroom_idx: "__total", __total: true });
      return rows;
    },
  },
  watch: {
    organizationIdx() {
      this.resetTabs();
      this.reload();
    },
  },
  created() {
    this.reload();
  },
  methods: {
    fmtKst, money, signedMoney, percent, isPositive, gradeLabel,
    ledgerTypeLabel, ledgerTypeKind, purchaseStatusLabel, purchaseStatusKind, memberRoleLabel, memberRoleKind,
    historyEventLabel, historyEventKind, historyChanges, historyDetailChips, historyActorLabel, historyActorType, purchaseCurrency,
    // 원장 한 줄 설명. 등록 차감·되돌림은 학생·클래스·교재, 구매 적립은 실입금을 적습니다(가이드 §2).
    ledgerDesc(row) {
      if (row.entry_type === "REGISTER" || row.entry_type === "REGISTER_REVERSAL") {
        return [row.student_name, row.classroom_name, row.textbook_title].filter(Boolean).join(" · ") || "-";
      }
      if (row.entry_type === "PURCHASE" && row.paid_amount != null) {
        return `실입금 ${money(row.paid_amount, row.paid_currency)}${row.purchase_idx ? ` · VP-${row.purchase_idx}` : ""}`;
      }
      if (row.entry_type === "PURCHASE_VOID" && row.purchase_idx) return `VP-${row.purchase_idx} 확정 취소`;
      return "-";
    },
    resetTabs() {
      this.history.loaded = false;
      this.ledger.loaded = false;
      this.purchases.loaded = false;
      this.members.loaded = false;
      this.invites.loaded = false;
      this.classStats.loaded = false;
    },
    async reload() {
      await this.loadDetail();
      this.resetTabs();
      this.loadTab(this.tab);
    },
    switchTab(id) {
      if (this.tab === id) return;
      this.tab = id;
      this.$router.replace({ query: { ...this.$route.query, tab: id } });
      this.loadTab(id);
    },
    loadTab(id) {
      if (id === "info" && !this.history.loaded) this.loadHistory();
      if (id === "balance") {
        this.loadBalance();
        if (!this.ledger.loaded) this.loadLedger();
      }
      if (id === "purchases" && !this.purchases.loaded) this.loadPurchases();
      if (id === "members") {
        if (!this.members.loaded) this.loadMembers();
        if (!this.invites.loaded) this.loadInvites();
      }
      if (id === "classes" && !this.classStats.loaded) {
        this.loadClassStats();
        this.loadClassrooms();
      }
    },
    async loadDetail() {
      this.loading = true;
      this.loadError = "";
      try {
        const res = await this.$axios({ method: "get", url: `/voucher/admin/organizations/${this.orgIdx}`, loadingBar: true });
        const b = bodyOf(res) || {};
        // 상세는 기관 테이블 컬럼 전부 + summary + members 입니다(docs/바우처_기관발행_관리자화면_가이드.md §3-1).
        this.org = b;
        this.summary = b.summary || null;
        // 상세에 실려 오는 members 는 활성 멤버입니다. 목록을 해제 · 전체로 보고 있으면 덮어쓰지 않습니다.
        if (Array.isArray(b.members) && this.members.status === "active") {
          this.members.items = b.members;
          this.members.loaded = true;
        }
      } catch (e) {
        console.error("기관 상세 조회 실패:", e);
        this.org = null;
        // 없는 기관이면 서버가 detail 객체와 함께 404 를 줍니다. 그 밖의 실패(라우트 없음 포함)는 카드 안에 이유를 그립니다.
        this.loadError = e?.response?.status === 404 && errorDetail(e) ? "" : loadErrorText(e);
      } finally {
        this.loading = false;
      }
    },
    async loadHistory() {
      this.history.loading = true;
      this.history.error = "";
      try {
        const H = this.history;
        const res = await this.$axios({
          method: "get",
          url: `/voucher/admin/organizations/${this.orgIdx}/history`,
          params: { page: H.page, size: H.size },
          loadingBar: false,
        });
        const p = pageOf(res, H.page);
        H.items = p.items;
        H.total = p.total;
        this.history.loaded = true;
      } catch (e) {
        console.error("기관 이력 조회 실패:", e);
        this.history.error = loadErrorText(e);
        this.history.items = [];
      } finally {
        this.history.loading = false;
      }
    },
    async loadBalance() {
      this.balance.loading = true;
      this.balance.error = "";
      try {
        const res = await this.$axios({ method: "get", url: `/voucher/admin/organizations/${this.orgIdx}/balance`, loadingBar: false });
        this.balance.data = bodyOf(res) || null;
      } catch (e) {
        console.error("잔액 조회 실패:", e);
        this.balance.error = loadErrorText(e);
      } finally {
        this.balance.loading = false;
      }
    },
    searchLedger() {
      const f = this.ledger.filters;
      if (f.date_from && f.date_to && f.date_from > f.date_to) {
        swal.fire("확인해 주세요", "시작일이 종료일보다 늦습니다.", "warning");
        return;
      }
      this.ledger.page = 1;
      this.loadLedger();
    },
    async loadLedger() {
      const L = this.ledger;
      L.loading = true;
      L.error = "";
      try {
        const params = { page: L.page, size: L.size };
        Object.entries(L.filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const res = await this.$axios({ method: "get", url: `/voucher/admin/organizations/${this.orgIdx}/ledger`, params, loadingBar: false });
        const p = pageOf(res, L.page);
        L.items = p.items;
        L.total = p.total;
        L.loaded = true;
      } catch (e) {
        console.error("원장 조회 실패:", e);
        L.error = loadErrorText(e);
        L.items = [];
        L.total = 0;
      } finally {
        L.loading = false;
      }
    },
    async loadPurchases() {
      this.purchases.loading = true;
      this.purchases.error = "";
      try {
        const res = await this.$axios({ method: "get", url: `/voucher/admin/organizations/${this.orgIdx}/purchases`, loadingBar: false });
        this.purchases.items = pageOf(res).items;
        this.purchases.loaded = true;
      } catch (e) {
        console.error("구매 내역 조회 실패:", e);
        this.purchases.error = loadErrorText(e);
        this.purchases.items = [];
      } finally {
        this.purchases.loading = false;
      }
    },
    async loadMembers() {
      this.members.loading = true;
      this.members.error = "";
      try {
        const res = await this.$axios({
          method: "get",
          url: `/voucher/admin/organizations/${this.orgIdx}/members`,
          params: { status: this.members.status },
          loadingBar: false,
        });
        this.members.items = pageOf(res).items;
        this.members.loaded = true;
      } catch (e) {
        console.error("멤버 조회 실패:", e);
        this.members.error = loadErrorText(e);
        this.members.items = [];
      } finally {
        this.members.loading = false;
      }
    },
    async loadClassStats() {
      const S = this.classStats;
      S.loading = true;
      S.error = "";
      try {
        const params = {};
        Object.entries(S.filters).forEach(([k, v]) => { if (v) params[k] = v; });
        const res = await this.$axios({ method: "get", url: `/voucher/org/${this.orgIdx}/stats/classrooms`, params, loadingBar: false });
        const b = bodyOf(res) || {};
        S.items = b.items || [];
        S.totals = b.totals || null;
        S.currency = b.currency_code || null;
        S.loaded = true;
      } catch (e) {
        console.error("클래스 통계 조회 실패:", e);
        S.error = loadErrorText(e);
        S.items = [];
        S.totals = null;
      } finally {
        S.loading = false;
      }
    },
    async loadClassrooms() {
      this.classrooms.loading = true;
      this.classrooms.error = "";
      try {
        const res = await this.$axios({
          method: "get",
          url: `/voucher/org/${this.orgIdx}/classrooms`,
          params: { status: "ongoing" },
          loadingBar: false,
        });
        this.classrooms.items = pageOf(res).items;
      } catch (e) {
        console.error("기관 클래스 조회 실패:", e);
        this.classrooms.error = loadErrorText(e);
        this.classrooms.items = [];
      } finally {
        this.classrooms.loading = false;
      }
    },

    // ── 기관 상태 ──
    async deactivate() {
      const r = await swal.fire({
        icon: "warning",
        title: "기관 비활성화",
        text: "Master 기능과 학생 등록이 멈춥니다. 남은 잔액은 반환으로 정리해 주세요. 기관은 삭제되지 않습니다.",
        input: "textarea",
        inputPlaceholder: "사유 (필수)",
        inputValidator: (v) => (!v || !v.trim() ? "사유를 입력해 주세요." : undefined),
        showCancelButton: true,
        confirmButtonText: "비활성화",
        cancelButtonText: "취소",
      });
      if (!r.isConfirmed) return;
      try {
        await this.$axios({ method: "post", url: `/voucher/admin/organizations/${this.orgIdx}/deactivate`, data: { reason: r.value.trim() }, loadingBar: true });
        swal.fire("비활성화했습니다", "", "success");
        this.reload();
      } catch (e) {
        swal.fire("처리하지 못했습니다", errorMessage(e, "비활성화하지 못했습니다."), "error");
      }
    },
    async activate() {
      const r = await swal.fire({ icon: "question", title: "기관을 다시 활성화합니다", showCancelButton: true, confirmButtonText: "활성화", cancelButtonText: "취소" });
      if (!r.isConfirmed) return;
      try {
        await this.$axios({ method: "post", url: `/voucher/admin/organizations/${this.orgIdx}/activate`, loadingBar: true });
        swal.fire("활성화했습니다", "", "success");
        this.reload();
      } catch (e) {
        swal.fire("처리하지 못했습니다", errorMessage(e, "활성화하지 못했습니다."), "error");
      }
    },

    // ── 입금 ──
    openDirect() {
      this.depositMode = "direct";
      this.depositTarget = null;
      this.depositOpen = true;
    },
    openConfirm(row) {
      this.depositMode = "confirm";
      this.depositTarget = { ...row, organization_idx: this.orgIdx, voucher_currency: this.currency };
      this.depositOpen = true;
    },
    onDeposited() {
      this.purchases.loaded = false;
      this.ledger.loaded = false;
      this.loadDetail();
      this.loadPurchases();
    },

    // ── 반환 · 정정 ──
    openMove(mode) {
      this.move = { open: true, mode, amount: "", payment_ref: "", memo: "", saving: false };
    },
    async submitMove() {
      const m = this.move;
      const re = m.mode === "adjust" ? SIGNED_DECIMAL : DECIMAL;
      if (!re.test(m.amount || "") || Number(m.amount) === 0) {
        swal.fire("확인해 주세요", m.mode === "adjust" ? "0 이 아닌 금액을 입력해 주세요(줄이려면 −)." : "0 보다 큰 금액을 입력해 주세요.", "warning");
        return;
      }
      if (!m.memo) {
        swal.fire("확인해 주세요", "사유를 입력해 주세요.", "warning");
        return;
      }
      const data = { amount: m.amount, memo: m.memo };
      if (m.mode === "withdraw" && m.payment_ref) data.payment_ref = m.payment_ref;
      m.saving = true;
      try {
        await this.$axios({
          method: "post",
          url: `/voucher/admin/organizations/${this.orgIdx}/balance/${m.mode}`,
          data,
          loadingBar: true,
        });
        m.open = false;
        swal.fire(m.mode === "adjust" ? "정정했습니다" : "반환을 기록했습니다", "", "success");
        this.loadDetail();
        this.loadBalance();
        this.loadLedger();
      } catch (e) {
        const code = errorCode(e);
        if (code === "VOUCHER_ADJUST_FORBIDDEN") {
          swal.fire("권한이 없습니다", "잔액 정정은 super_admin · finance_manager 만 할 수 있습니다.", "warning");
        } else if (code === "VOUCHER_BALANCE_SHORT") {
          swal.fire("잔액이 모자랍니다", errorMessage(e, "반환액이 현재 잔액보다 큽니다."), "warning");
        } else {
          swal.fire("처리하지 못했습니다", errorMessage(e, "요청을 처리하지 못했습니다."), "error");
        }
      } finally {
        m.saving = false;
      }
    },

    // ── 멤버 ──
    /** 해제된 멤버 행. 서버가 active_yn='N' + deactivated_at 로 표시합니다(2026-09-23). */
    isReleasedMember(row) {
      return row?.active_yn === "N" || !!row?.deactivated_at;
    },
    // ── 강사 등록 요청 (동의 코드) ──
    inviteStatusLabel(s) {
      return INVITE_STATUS[s]?.label || s || "-";
    },
    inviteStatusKind(s) {
      return INVITE_STATUS[s]?.kind || "default";
    },
    openInvite(row = null) {
      this.inviteModal = { open: true, invite: row ? { ...row } : null };
    },
    // 요청 · 재발송 · 확인 · 취소 뒤에 대기 목록을 새로 읽습니다. 확인이 끝났으면 멤버 목록도 바뀝니다.
    onInviteDone() {
      this.loadInvites();
      this.loadMembers();
      this.loadDetail();
    },
    async loadInvites() {
      const I = this.invites;
      I.loading = true;
      I.error = "";
      try {
        const res = await this.$axios({
          method: "get",
          url: `/voucher/org/${this.orgIdx}/instructors/invites`,
          params: { status: I.status },
          loadingBar: false,
        });
        I.items = pageOf(res).items;
        I.loaded = true;
      } catch (e) {
        console.error("등록 요청 조회 실패:", e);
        I.items = [];
        I.error = loadErrorText(e);
      } finally {
        I.loading = false;
      }
    },
    async cancelInvite(row) {
      const r = await swal.fire({
        icon: "question",
        title: "등록 요청을 취소합니다",
        text: `${row.email} 로 보낸 코드는 더 이상 쓸 수 없습니다.`,
        showCancelButton: true,
        confirmButtonText: "요청 취소",
        cancelButtonText: "닫기",
      });
      if (!r.isConfirmed) return;
      try {
        await this.$axios({
          method: "post",
          url: `/voucher/org/${this.orgIdx}/instructors/invites/${row.invite_idx}/cancel`,
          loadingBar: true,
        });
        this.loadInvites();
      } catch (e) {
        // 이미 확인 · 취소 · 만료된 요청이면 목록이 낡은 것이라 다시 읽습니다.
        const code = errorCode(e);
        if (code === "ORG_INVITE_NOT_PENDING" || code === "ORG_INVITE_NOT_FOUND") this.loadInvites();
        swal.fire("취소하지 못했습니다", errorMessage(e, "요청을 취소하지 못했습니다."), "error");
      }
    },
    async changeRole(row) {
      const next = row.role === "master" ? "instructor" : "master";
      const r = await swal.fire({
        icon: "question",
        title: `${row.name || '이 강사'}를 ${memberRoleLabel(next)}(으)로 바꿉니다`,
        input: "text",
        inputPlaceholder: "사유 (선택)",
        showCancelButton: true,
        confirmButtonText: "변경",
        cancelButtonText: "취소",
      });
      if (!r.isConfirmed) return;
      const data = { role: next };
      if (r.value && r.value.trim()) data.reason = r.value.trim();
      try {
        await this.$axios({ method: "put", url: `/voucher/admin/organizations/${this.orgIdx}/members/${row.users_idx}`, data, loadingBar: true });
        this.loadMembers();
        this.loadDetail();
      } catch (e) {
        if (errorCode(e) === "ORG_MEMBER_NOT_INSTRUCTOR") swal.fire("승인 강사가 아닙니다", "강사 권한이 없는 계정은 역할을 바꿀 수 없습니다.", "warning");
        else swal.fire("변경하지 못했습니다", errorMessage(e, "역할을 바꾸지 못했습니다."), "error");
      }
    },
    async removeMember(row) {
      const r = await swal.fire({
        icon: "warning",
        title: `${row.name || '이 멤버'} 해제`,
        text: row.role === "master" ? "Master 를 해제하면 이 사람은 기관 바우처를 관리할 수 없습니다." : "이 기관의 강사에서 뺍니다.",
        input: "textarea",
        inputPlaceholder: "사유 (필수)",
        inputValidator: (v) => (!v || !v.trim() ? "사유를 입력해 주세요." : undefined),
        showCancelButton: true,
        confirmButtonText: "해제",
        cancelButtonText: "취소",
      });
      if (!r.isConfirmed) return;
      const reason = r.value.trim();
      const call = (closeVouchers) =>
        this.$axios({
          method: "delete",
          url: `/voucher/admin/organizations/${this.orgIdx}/members/${row.users_idx}`,
          params: { reason, close_vouchers: closeVouchers },
          loadingBar: true,
        });
      try {
        try {
          await call(false);
        } catch (e) {
          // 이 강사 클래스에 진행 중 지정이 있으면 목록을 보여 주고, 확인하면 지정까지 종료하며 다시 해제합니다.
          if (errorCode(e) !== "ORG_MEMBER_HAS_ACTIVE_VOUCHERS") throw e;
          if (!(await confirmCloseVouchers(errorDetail(e)))) return;
          await call(true);
        }
        swal.fire("해제했습니다", "", "success");
        this.loadMembers();
        this.loadDetail();
      } catch (e) {
        swal.fire("해제하지 못했습니다", errorMessage(e, "멤버를 해제하지 못했습니다."), "error");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.vod {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__tab-icon { font-size: 15px !important; }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 32px;
  }

  &__banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    font-size: 13px;

    i { font-size: 18px; }

    &--err { background: $p-err-soft; color: $p-err-ink; border: 1px solid $p-err; }
    &--muted { background: $p-panel-alt; color: $p-ink-mid; border: 1px solid $p-line; }
  }

  &__dl {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 24px;
    margin: 0;

    @media (max-width: 768px) { grid-template-columns: 1fr; }

    > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }

    dt { font-size: 11px; font-weight: 800; color: $p-ink-mid; }
    dd { margin: 0; font-size: 13px; color: $p-ink; }
  }

  &__pre { white-space: pre-wrap; }

  &__more {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    color: $p-info-ink;

    i { font-size: 16px; }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;

    > * {
      padding: 14px;
      background: $p-panel;
      border: 1px solid $p-line;
      border-radius: $p-radius-lg;
    }
  }

  &__stat-badges { margin-top: 4px; }

  &__stat-sub {
    margin-top: 4px;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__stat-err {
    margin-top: 4px;
    font-size: 11px;
    font-weight: 700;
    color: $p-err-ink;
  }

  &__filters {
    display: grid;
    grid-template-columns: repeat(3, minmax(140px, 220px)) auto;
    gap: 12px;
    align-items: end;
    margin-bottom: 14px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__filter-actions { display: flex; gap: 6px; }

  &__mini-select {
    width: auto;
    height: 30px;
    padding: 0 26px 0 10px;
    font-size: 12px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }

  &__stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    &--center { align-items: center; }
  }

  &__strong { font-weight: 700; color: $p-ink; }

  &__sub {
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__small { font-size: 11px; }

  &__mono {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
  }

  &__pos { color: $p-ok-ink; }
  &__neg { color: $p-err-ink; }
  &__dash { color: $p-ink-softer; }

  &__changes {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    align-items: center;
  }

  &__change {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: 7px;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    font-size: 11px;
    color: $p-ink-mid;

    b { color: $p-ink; }
  }

  &__note {
    margin: 12px 0 0;
    font-size: 12px;
    color: $p-ink-mid;

    &--top { margin: 0 0 12px; }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__search {
    display: flex;
    gap: 6px;
  }

  &__results {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 260px;
    overflow-y: auto;
  }

  &__result {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid $p-line;
    border-radius: $p-radius;
    cursor: pointer;
    font-size: 13px;

    &.is-on { border-color: $p-ink; background: $p-point-soft; }
  }
}
</style>
